// S3 service for uploading play GIFs
import { S3Client, PutObjectCommand } from '@aws-sdk/client-s3'
import { getCurrentUser, fetchAuthSession } from 'aws-amplify/auth'
import { getHashedUserId } from '../utils/idUtils.js'

// Initialize S3 client
let s3Client = null

const getS3Client = async () => {
  if (s3Client) return s3Client
  
  // Check if Identity Pool is configured
  const identityPoolId = import.meta.env.VITE_AWS_IDENTITY_POOL_ID
  if (!identityPoolId) {
    throw new Error('Identity Pool ID is not configured. Please set VITE_AWS_IDENTITY_POOL_ID in your .env file.')
  }
  
  try {
    // First, verify user is authenticated
    try {
      await getCurrentUser()
    } catch (authError) {
      throw new Error('User is not authenticated. Please log in first.')
    }
    
    // Fetch auth session - force refresh to ensure we get Identity Pool credentials
    const session = await fetchAuthSession({ forceRefresh: true })
    const credentials = session.credentials
    
    if (!credentials || !credentials.accessKeyId) {
      throw new Error('No AWS credentials available. Please ensure:\n1. Identity Pool is configured correctly\n2. You are authenticated\n3. Identity Pool is linked to your User Pool\n4. Try signing out and signing back in')
    }
    
    const client = new S3Client({ 
      region: import.meta.env.VITE_AWS_REGION || 'us-east-2',
      credentials: {
        accessKeyId: credentials.accessKeyId,
        secretAccessKey: credentials.secretAccessKey,
        sessionToken: credentials.sessionToken
      }
    })
    
    s3Client = client
    return s3Client
  } catch (error) {
    // Re-throw with more context if it's our custom error
    if (error.message.includes('Identity Pool') || error.message.includes('credentials')) {
      throw error
    }
    
    throw new Error(`Failed to get AWS credentials: ${error.message}. Please check your Identity Pool configuration.`)
  }
}

const BUCKET_NAME = import.meta.env.VITE_S3_GIFS_BUCKET_NAME || 'soccer-play-designer-gifs'
const REGION = import.meta.env.VITE_AWS_REGION || 'us-east-2'
// Use custom CDN domain if configured, otherwise fall back to S3 direct URL
const GIFS_CDN_DOMAIN = import.meta.env.VITE_GIFS_CDN_DOMAIN || `${BUCKET_NAME}.s3.${REGION}.amazonaws.com`

// Get current user ID (Identity Pool identity ID - full format)
const getIdentityId = async () => {
  try {
    // First verify user is authenticated
    await getCurrentUser()
    
    // Get Identity Pool identity ID from session
    const session = await fetchAuthSession({ forceRefresh: true })
    
    if (!session.identityId) {
      throw new Error('Identity Pool identity ID not available. Please ensure:\n1. Identity Pool is properly linked to your User Pool in AWS Console\n2. You have signed out and signed back in after configuring Identity Pool\n3. Identity Pool ID is correctly set in .env file')
    }
    
    return session.identityId
  } catch (error) {
    if (error.message.includes('Identity Pool') || error.message.includes('identity ID')) {
      throw error
    }
    throw new Error('User not authenticated')
  }
}

// Get hashed user ID for S3 paths (strips region prefix and hashes to 12 chars)
const getUserId = async () => {
  const identityId = await getIdentityId()
  return await getHashedUserId(identityId)
}

// Upload GIF to S3
export const uploadGifToS3 = async (gifDataUrl, playId) => {
  try {
    const userId = await getUserId()
    const client = await getS3Client()
    
    // Convert data URL to ArrayBuffer, then to Uint8Array
    const response = await fetch(gifDataUrl)
    const arrayBuffer = await response.arrayBuffer()
    const uint8Array = new Uint8Array(arrayBuffer)
    
    // Create S3 key: gifs/{userId}/{playId}.gif
    const key = `gifs/${userId}/${playId}.gif`
    
    // Upload to S3
    const command = new PutObjectCommand({
      Bucket: BUCKET_NAME,
      Key: key,
      Body: uint8Array,
      ContentType: 'image/gif',
      CacheControl: 'public, max-age=31536000', // Cache for 1 year
      ServerSideEncryption: 'AES256'
    })
    
    await client.send(command)
    
    // Return public URL using CDN domain if configured
    const publicUrl = `https://${GIFS_CDN_DOMAIN}/${key}`
    
    return {
      url: publicUrl,
      key: key
    }
  } catch (error) {
    console.error('Error uploading GIF to S3:', error)
    throw new Error('Failed to upload GIF to S3: ' + error.message)
  }
}

// Get GIF URL for a play
// Note: userId should be the hashed version (12 chars) for new URLs
// This function can handle both old (full identityId) and new (hashed) formats
export const getGifUrl = async (identityId, playId) => {
  // If identityId contains a colon, it's the old format - hash it
  // Otherwise, assume it's already hashed
  const userId = identityId.includes(':') 
    ? await getHashedUserId(identityId)
    : identityId
  
  return `https://${GIFS_CDN_DOMAIN}/gifs/${userId}/${playId}.gif`
}

